import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { addAlert, selectLoading } from "src/modules/notification/redux";
import {
  updateUnassign,
  updateUnassignUrl,
  validateUnassignUrl,
  validateUnassignRange,
} from "src/modules/companies/redux";
import UnassignPanelComponent from "./unassignPanel.component";

export default function UnassignPanelContainer({
  selectedCompany,
  onSerialNumberAssignDialogCloseClick,
}) {
  const dispatch = useDispatch();

  const isValidatingUrl = useSelector((state) =>
    selectLoading(state, validateUnassignUrl.typePrefix)
  );
  const isValidatingRange = useSelector((state) =>
    selectLoading(state, validateUnassignRange.typePrefix)
  );
  const isUpdatingRange = useSelector((state) =>
    selectLoading(state, updateUnassign.typePrefix)
  );
  const isUpdatingUrl = useSelector((state) =>
    selectLoading(state, updateUnassignUrl.typePrefix)
  );

  const isValidating = isValidatingUrl || isValidatingRange;
  const isUpdating = isUpdatingRange || isUpdatingUrl;

  const [selectedMethod, setSelectedMethod] = useState("serial_num");
  const [urlInput, setUrlInput] = useState("");
  const [validatedItems, setValidatedItems] = useState([]);
  const [originalRanges, setOriginalRanges] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [hasValidated, setHasValidated] = useState(false);
  const [hasModifiedAfterValidation, setHasModifiedAfterValidation] = useState(false);

  // Local state to enforce minimum backdrop display duration
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);

  // Local state to enforce minimum validation button loading duration
  const [isValidatingLocal, setIsValidatingLocal] = useState(false);

  const handleMethodChange = (event) => {
    setSelectedMethod(event.target.value);
    setUrlInput("");
    setValidatedItems([]);
    setHasValidated(false);
    setHasModifiedAfterValidation(false);
    setOriginalRanges([]);
  };

  const handleFieldChange = () => {
    setHasModifiedAfterValidation(true);
  };

  const handleValidateUrls = async () => {
    if (!urlInput.trim()) return;

    const urls = urlInput
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) return;

    // Show loading and track start time
    const startTime = Date.now();
    const minDisplayTime = 500; // Minimum 500ms display time
    setIsValidatingLocal(true);
    setValidatedItems([]); // Clear table to show "No Data"
    setHasValidated(false); // Reset validation flag

    try {
      const action = await dispatch(validateUnassignUrl({
        node_id: selectedCompany.company.id,
        urls,
      }));
      const result = unwrapResult(action);
      const items = (result.data.urls || []).map((item, idx) => ({
        ...item,
        index: idx,
        type: "url",
      }));

      // Ensure minimum display time before hiding loading
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        setIsValidatingLocal(false);
        setValidatedItems(items);
        setHasValidated(true);
        setHasModifiedAfterValidation(false);
      }, remainingTime);
    } catch (error) {
      // Ensure minimum display time even on error
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        setIsValidatingLocal(false);
        dispatch(
          addAlert({
            severity: "error",
            message: error.message,
          })
        );
      }, remainingTime);
    }
  };

  const handleValidateRange = async (ranges) => {
    setOriginalRanges(ranges);

    // Show loading and track start time
    const startTime = Date.now();
    const minDisplayTime = 500; // Minimum 500ms display time
    setIsValidatingLocal(true);
    setValidatedItems([]); // Clear table to show "No Data"
    setHasValidated(false); // Reset validation flag

    try {
      const actions = await dispatch(
        validateUnassignRange({
          node_id: selectedCompany.company.id,
          type: selectedMethod,
          ranges: ranges.map((r) => ({ from: r.from, to: r.to })),
        })
      );

      const results = unwrapResult(actions);
      const responseData = results.data || results;

      const allItems = [];
      let globalIndex = 0;

        if (Array.isArray(responseData)) {
          responseData.forEach((requestGroup) => {
            const requestIndex = requestGroup.request_index;
            const segments = requestGroup.segments || [];

        if (segments.length === 0) {
          const originalRange = ranges[requestIndex - 1] || ranges[0];
          allItems.push({
            index: globalIndex++,
            type: selectedMethod,
            requestIndex: requestIndex || 0,
            batch_number: "-",
            from_serial: originalRange?.from || "-",
            to_serial: originalRange?.to || "-",
            from_seq: originalRange?.from || "-",
            to_seq: originalRange?.to || "-",
            product_name: originalRange?.product_name || "-",
            product_attributes: [],
            status: 0,
            reason: "not_found",
          });
        } else {
          // Process normal segments
          segments.forEach((segment) => {
            allItems.push({
              ...segment,
              index: globalIndex++,
              type: selectedMethod,
              requestIndex: requestIndex || 0,
              batch_number: segment.batch_number || "-",
              from_serial: segment.from_serial || "-",
              to_serial: segment.to_serial || "-",
              from_seq: segment.from_seq || "-",
              to_seq: segment.to_seq || "-",
              product_name: segment.product_name || "-",
              status: segment.status,
              reason: segment.reason || null,
            });
          });
        }
      });
    }

    // Ensure minimum display time before hiding loading
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, minDisplayTime - elapsed);

    setTimeout(() => {
      setIsValidatingLocal(false);
      setValidatedItems(allItems);
      setHasValidated(true);
      setHasModifiedAfterValidation(false);
    }, remainingTime);
  } catch (error) {
    // Ensure minimum display time even on error
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, minDisplayTime - elapsed);

    setTimeout(() => {
      setIsValidatingLocal(false);
      dispatch(
        addAlert({
          severity: "error",
          message: error.message || "Validation failed",
        })
      );
    }, remainingTime);
  }
};

  const handleSubmit = () => setShowConfirmDialog(true);
  const handleCancelDialog = () => setShowConfirmDialog(false);

  const handleConfirmUnassign = async () => {
    setShowConfirmDialog(false);

    const validItems = validatedItems.filter((item) => item.status === 1);

    if (validItems.length === 0) {
      dispatch(
        addAlert({
          severity: "warning",
          message: "No valid items to unassign.",
        })
      );
      return;
    }

    // Show backdrop and track start time
    const startTime = Date.now();
    const minDisplayTime = 500; // Minimum 500ms display time
    setIsBackdropOpen(true);

    try {
    if (selectedMethod === "url") {
     const urls = validItems.map((item) => item.url || item.code);
      const action = await dispatch(
        updateUnassignUrl({
          node_id: selectedCompany.company.id,
          urls,
        })
      );

        const result = unwrapResult(action);
        const totalUnassigned = result?.data?.total_unassigned || result?.total_unassigned || urls.length;

        // Ensure minimum display time before hiding backdrop
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);

        setTimeout(() => {
          setIsBackdropOpen(false);
          dispatch(
            addAlert({
              severity: "success",
              message: `Successfully unassigned ${totalUnassigned} URL(s).`,
            })
          );
        }, remainingTime);

        setValidatedItems([]);
        setHasValidated(false);
        setHasModifiedAfterValidation(false);
      } else {
        const groupedData = {};

        validItems.forEach((item) => {
          const reqIndex = item.requestIndex || 0;
          if (!groupedData[reqIndex]) {
            groupedData[reqIndex] = {
              request_index: reqIndex,
              segments: [],
            };
          }

          let fromValue;
          let toValue;
          if (selectedMethod === "serial_num") {
            fromValue = item.from_serial;
            toValue = item.to_serial;
          } else if (selectedMethod === "seq_num") {
            fromValue = item.from_seq;
            toValue = item.to_seq;
          }

          groupedData[reqIndex].segments.push({
            from: fromValue,
            to: toValue,
          });
        });

        const dataArray = Object.values(groupedData);

        const action = await dispatch(
          updateUnassign({
            node_id: selectedCompany.company.id,
            type: selectedMethod,
            data: dataArray,
          })
        );

        const result = unwrapResult(action);
        const totalUnassigned = result?.data?.total_unassigned || result?.total_unassigned || 0;

        // Ensure minimum display time before hiding backdrop
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsed);

        setTimeout(() => {
          setIsBackdropOpen(false);
          dispatch(
            addAlert({
              severity: "success",
              message: `Successfully unassigned ${totalUnassigned} serial number(s).`,
            })
          );
        }, remainingTime);

        setValidatedItems([]);
        setHasValidated(false);
        setHasModifiedAfterValidation(false);
      }
    } catch (error) {
      let errorMessage = error.message || "Failed to Unassign";

      if (error.data && error.data.data) {
        const validationErrors = error.data.data;
        if (validationErrors.urls && Array.isArray(validationErrors.urls)) {
          errorMessage = `Validation Error: ${validationErrors.urls.join(",")}`;
        } else if (typeof validationErrors === "object") {
          const errorFields = Object.keys(validationErrors);
          if (errorFields.length > 0) {
            errorMessage = `Validation Error: ${errorFields.map((field) => `${field}: ${validationErrors[field]}`).join(", ")}`;
          }
        }
      }

      // Ensure minimum display time even on error
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        setIsBackdropOpen(false);
        dispatch(
          addAlert({
            severity: "error",
            message: errorMessage,
          })
        );
      }, remainingTime);
    }
  };

  const validItemsCount = validatedItems.filter((item) => item.status === 1).length;

  const isUpdateButtonDisabled =
    !hasValidated ||
    hasModifiedAfterValidation ||
    validItemsCount === 0 ||
    isBackdropOpen ||
    isValidatingLocal;

  return (
    <UnassignPanelComponent
      selectedCompany={selectedCompany}
      onSerialNumberAssignDialogCloseClick={onSerialNumberAssignDialogCloseClick}
      isUpdating={isBackdropOpen}
      isUpdateButtonDisabled={isUpdateButtonDisabled}
      selectedMethod={selectedMethod}
      handleMethodChange={handleMethodChange}
      urlInput={urlInput}
      setUrlInput={setUrlInput}
      handleFieldChange={handleFieldChange}
      handleValidateUrls={handleValidateUrls}
      handleValidateRange={handleValidateRange}
      validatedItems={validatedItems}
      isValidating={isValidatingLocal}
      showConfirmDialog={showConfirmDialog}
      handleConfirmUnassign={handleConfirmUnassign}
      handleCancelDialog={handleCancelDialog}
      validItemsCount={validItemsCount}
      handleSubmit={handleSubmit}
    />
  );
}
